import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { Team } from '../../../../state/reduxRepositories/teams/team.interfaces';
import { Player } from '../../../../state/reduxRepositories/players/player.interfaces';
import { TeamSummaryStats } from './teamSummaryStats.dumb';
import { getInitials } from '../../../../helpers/initialsExtractor';
import { Role } from '../../../../state/reduxRepositories/users/users.interfaces';
import { LoggedInUser } from '../../../../state/helperServices/auth/auth.interfaces';
import { Avatar } from '../../../../components/avatar/avatar';

interface SummaryCardProps {
  team: Team;
  onClick(): void;
  players: Player[];
  onRemoveTeam?: any;
  onEditTeam: (team: Team) => void;
  updateTeam?: any;
  currentLogInUser?: LoggedInUser;
  dispatchSelectedTeam: (team: Team) => void;
}

export function TeamSummaryCard({
  team,
  onClick,
  players,
  onRemoveTeam,
  onEditTeam,
  updateTeam,
  currentLogInUser,
  dispatchSelectedTeam,
}: SummaryCardProps): JSX.Element {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const clearedPlayers = team?.players.filter(
    (player) => player.clearedToPlay,
  ).length;
  const notClearedPlayers = team?.players.filter(
    (player) => !player.clearedToPlay,
  ).length;

  const handleDelete = () => {
    handleClose();
    dispatchSelectedTeam(team);
    onRemoveTeam(team?.id, team?.playerIds, team?.name);
  };

  const handleEdit = () => {
    handleClose();
    dispatchSelectedTeam(team);
    onEditTeam(team);
  };

  // TODO: Full card onClick issue due to action item

  return (
    <>
      <Card sx={{ borderRadius: 4 }}>
        <CardHeader
          avatar={<Avatar title={getInitials(team?.name)} />}
          sx={{
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          action={
            <>
              {currentLogInUser?.role.includes(Role.ADMIN) && (
                <IconButton
                  id="more-button"
                  data-testid={`more-button-${team?.name?.replace(/\s/g, '')}`}
                  aria-label={t('accessibility.SETTINGS')}
                  sx={{ m: 1 }}
                  onClick={handleClick}
                >
                  <MoreVertIcon />
                </IconButton>
              )}
            </>
          }
          title={
            <Typography data-testid="teamName" onClick={onClick}>
              {team.name}
            </Typography>
          }
          titleTypographyProps={{ variant: 'h6' }}
          subheaderTypographyProps={{ variant: 'subtitle2' }}
          subheader={
            <TeamSummaryStats
              cleared={clearedPlayers}
              notCleared={notClearedPlayers}
              teamPlayers={team?.players}
            />
          }
        />
      </Card>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
      >
        <MenuItem onClick={handleEdit}>{t('EDIT')}</MenuItem>
        <MenuItem onClick={handleDelete}>{t('DELETE')}</MenuItem>
      </Menu>
    </>
  );
}
